import { Tabs, Tab } from "@mui/material";
import { useState, SyntheticEvent, useEffect, useCallback } from "react";
import { Link, matchRoutes, RouteMatch, RouteObject, useLocation, useParams } from "react-router-dom";
import Routes from "constants/routes";
import { ITabItem } from "pages/models";
import { AppPermissions } from "constants/permissions";
import { useIsAllowed, When } from "@sanmanuel/permissions";

const INITIAL_TAB = {
  title: "overview",
  path: Routes.overview,
  permissionsRequired: [
    AppPermissions.view.playerInfo,
    AppPermissions.view.playerStats,
    AppPermissions.view.limitedPlayerInfo,
  ],
};

const baseTabs: ITabItem[] = [
  INITIAL_TAB,
  {
    title: "notes",
    path: Routes.notes,
    children: [{ path: Routes.viewNote }],
    permissionRequired: [AppPermissions.view.notes],
  },
  { title: "credit", path: Routes.credits, permissionRequired: AppPermissions.view.credit },
  { title: "net rev", path: Routes.netRevenue, permissionRequired: AppPermissions.view.playerStats },
  { title: "offers", path: Routes.offers, permissionRequired: AppPermissions.view.offers },
  { title: "trips", path: Routes.trips, permissionRequired: AppPermissions.view.trips },
  { title: "comps", path: Routes.comps, permissionRequired: AppPermissions.view.comps },
  {
    title: "Promos & Events",
    path: Routes.eventsAndPromotions,
    permissionRequired: [AppPermissions.view.promotions, AppPermissions.view.events],
  },
  {
    title: "stays",
    path: Routes.stays,
    children: [
      { path: Routes.currentStays },
      { path: Routes.previousStays },
      { path: Routes.upcomingStays },
      { path: Routes.cancelledStays },
      { path: Routes.viewStay },
    ],
    permissionRequired: AppPermissions.view.hotel,
  },
  {
    title: "Mobile App",
    path: Routes.mobileApp,
    permissionRequired: [AppPermissions.view.mobile, AppPermissions.view.clubSerranoOnline],
  },
];

const noTab = { path: Routes.player };

const useTabs = () => {
  const isAllowed = useIsAllowed();
  const buildTabs = useCallback(() => {
    const newTabs = baseTabs.filter((tab) =>
      tab.permissionRequired ? isAllowed(tab.permissionRequired, When.any) : true
    );
    return newTabs;
  }, [isAllowed]);

  const [tabs, setTabs] = useState<ITabItem[]>(buildTabs());

  useEffect(() => {
    setTabs(buildTabs());
  }, [buildTabs]);

  return tabs;
};

function isArrayOfStrings(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

export const firstMatchingRoute = (
  routes: (RouteObject | string)[],
  locationArg: string | Partial<Location>,
  basename?: string | undefined
): RouteMatch | undefined => {
  let actualRoutes = routes as RouteObject[];
  if (isArrayOfStrings(routes)) {
    actualRoutes = routes.map((r) => ({ path: r }));
  }
  const match = matchRoutes(actualRoutes, locationArg, basename);
  if (!match || match.length === 0) {
    return;
  }
  return match[0];
};

export const PlayerPageTabs = () => {
  const tabs = useTabs();
  const [currentTab, setCurrentTab] = useState<string | boolean>(false);
  let params = useParams();
  let location = useLocation();

  useEffect(() => {
    if (!tabs) return;
    const match = firstMatchingRoute([...tabs, noTab], location);
    if (!match) {
      setCurrentTab(false);
      return;
    }
    const path = match.route.path;
    if (!path) {
      return setCurrentTab(false);
    }
    if (path === noTab.path) {
      setCurrentTab(Routes.tabForPlayer(INITIAL_TAB.path));
      return;
    }
    setCurrentTab(Routes.tabForPlayer(path));
  }, [location.pathname, params.playerId, tabs]);

  const handleChange = (e: SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  return (
    <Tabs
      value={currentTab}
      onChange={handleChange}
      variant="scrollable"
      scrollButtons={true}
      allowScrollButtonsMobile={true}
    >
      {tabs?.map((tab) => {
        const tabName = Routes.tabForPlayer(tab.path);
        const key = `${tab.title}-tab`;
        return <Tab key={key} label={tab.title} to={tabName} value={tabName} component={Link} />;
      })}
    </Tabs>
  );
};
